import React, { useState } from 'react'
import { AuthHead, AuthNotice, AuthPage } from './auth-card'

// The invitations step after a single sign-on (owner ruling 2026-09-27):
// every pending invite for the account -- from any organization -- with
// what it gives, and Accept / Decline for each. Nothing is decided for the
// person: an invitation's roles, groups and courses apply only when they
// accept it. It follows the first-sign-in consent pages (/open/register,
// /open/link, open-account-consent.js) in the same auth-card language.
//
// The same cards serve /auth/invitations (invitations while signed in, from
// the account menu): the page passes its own title, lede and button label.
// An accepted card offers "Open <org>" when the page can switch into it
// (onOpen).

const list = items => (items || []).filter(Boolean).join(', ')

const InviteItem = ({ invite, decision, busy, opening, onAccept, onDecline, onOpen }) => {
  const org = invite.organizationName || 'An organization'
  const roles = list(invite.roles)

  return (
    <li className="sa-invite">
      <p className="m-0">
        <strong className="sa-name">{org}</strong> invited you{roles ? <> as <strong>{roles}</strong></> : null}.
      </p>
      {list(invite.courses) && <p className="m-0 sw-hint">Courses: {list(invite.courses)}</p>}
      {list(invite.groups) && <p className="m-0 sw-hint">Groups: {list(invite.groups)}</p>}
      {invite.member &&
        <p className="m-0 sw-hint">You're already a member through your organization's sign-in. Accepting adds this access.</p>}

      {decision === 'accepted' &&
        <>
          <p className="m-0" role="status">{`Accepted. You have this access in ${org}.`}</p>
          {onOpen &&
            <div className="sa-invite-actions">
              <button type="button" className="btn sw-btn-primary" disabled={opening} onClick={onOpen}>
                {opening ? `Opening ${org}…` : `Open ${org}`}
              </button>
            </div>}
        </>}
      {decision === 'declined' && <p className="m-0" role="status">Declined. Nothing from this invitation was added.</p>}
      {decision
        ? null
        : <div className="sa-invite-actions">
            <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={onAccept} aria-label={`Accept ${org}`}>
              Accept
            </button>
            <button type="button" className="btn sw-btn" disabled={busy} onClick={onDecline} aria-label={`Decline ${org}`}>
              Decline
            </button>
          </div>}
    </li>
  )
}

const SIGN_IN_LEDE = 'Your sign-in worked. These organizations invited you to EducationXR. Nothing changes until you accept.'

// onDecide(invite, accept) returns a promise; onContinue moves on; onOpen
// (optional, returns a promise) switches into an accepted invite's
// organization. onDecided(invite, accept) hears each answer.
export const InviteConfirmation = ({
  invites,
  onDecide,
  onContinue,
  onOpen,
  onDecided,
  title = 'Your invitations',
  lede = SIGN_IN_LEDE,
  continueLabel = 'Continue',
  children
}) => {
  const [decisions, setDecisions] = useState({})
  const [busy, setBusy] = useState(null)
  const [failed, setFailed] = useState(null)
  const [opening, setOpening] = useState(null)
  const [openFailed, setOpenFailed] = useState(null)

  const open = async invite => {
    setOpening(invite.userID)
    setOpenFailed(null)
    try {
      await onOpen(invite)
    } catch {
      setOpenFailed(invite.organizationName || 'that organization')
      setOpening(null)
    }
  }

  const decide = async (invite, accept) => {
    setBusy(invite.userID)
    setFailed(null)
    try {
      await onDecide(invite, accept)
      setDecisions(prev => ({ ...prev, [invite.userID]: accept ? 'accepted' : 'declined' }))
      onDecided && onDecided(invite, accept)
    } catch {
      setFailed(invite.organizationName || 'that organization')
    } finally {
      setBusy(null)
    }
  }

  const undecided = invites.filter(i => !decisions[i.userID]).length

  return (
    <AuthPage labelledBy="invites-title">
      <AuthHead id="invites-title" title={title} lede={lede} />

      {children}

      {failed &&
        <AuthNotice tone="bad" role="alert">
          <p>We couldn't record your answer for {failed}. The invitation may have expired or been withdrawn.</p>
        </AuthNotice>}

      {openFailed &&
        <AuthNotice tone="bad" role="alert">
          <p>We couldn't open {openFailed}. Try again, or use Switch organization.</p>
        </AuthNotice>}

      <ul className="sa-invites" aria-labelledby="invites-title">
        {invites.map(invite =>
          <InviteItem
            key={invite.userID}
            invite={invite}
            decision={decisions[invite.userID]}
            busy={busy === invite.userID}
            opening={opening === invite.userID}
            onOpen={onOpen ? () => open(invite) : undefined}
            onAccept={() => decide(invite, true)}
            onDecline={() => decide(invite, false)}
          />
        )}
      </ul>

      <button type="button" className={`btn ${undecided ? 'sw-btn' : 'sw-btn-primary'} sa-block`} onClick={onContinue}>
        {continueLabel}
      </button>
      {undecided > 0 &&
        <p className="sa-hint">Invitations you don't answer now stay open until they expire.</p>}
    </AuthPage>
  )
}
